import { FC } from 'react';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Variant as VariantType } from 'shared/types/product';
import Typography from 'components/commercetools-ui/atoms/typography';
import Button from 'components/commercetools-ui/atoms/button';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { textToColor } from '../../../../../../helpers/textToColor/textToColor';
import { extractAttributeName } from 'helpers/stringHelpers/extractAttributeName';

type ProductAttributeProps = {
  className?: string;
  currentVariant: VariantType;
  attribute: string;
  isColorAttribute?: boolean;
  inModalVersion?: boolean;
  selectedOption?: string; // Optional value that overrides the selected option calculated based on the current variant chosen.
  options: string[];
  onClick?: (attribute: string, value: string) => void;
};

const ColorBackground = ({ backgroundColor }: { backgroundColor: string }) => (
  <div className="h-32 w-32 border border-neutral-400" style={{ backgroundColor }} />
);

const DropdownButton = ({
  selectedOption,
  isColorAttribute,
  backgroundColor,
}: {
  selectedOption: string;
  isColorAttribute: boolean;
  backgroundColor?: string;
}) => (
  <div className="flex w-full cursor-pointer items-center justify-between bg-white">
    <div className="flex items-center gap-8">
      {isColorAttribute && backgroundColor && <ColorBackground backgroundColor={backgroundColor} />}
      <Typography className="body-text-small text-14 text-primary-black">{selectedOption}</Typography>
    </div>
    <ChevronDownIcon strokeWidth={2} className="w-16 text-secondary-black" />
  </div>
);

const ProductAttribute: FC<ProductAttributeProps> = ({
  className,
  currentVariant,
  options,
  selectedOption,
  attribute,
  isColorAttribute = false,
  inModalVersion,
  onClick,
}) => {
  const { formatMessage } = useFormat({ name: 'product' });

  const attributeString = attribute.toString();

  const labelClassName = useClassNames([
    'capitalize leading-loose font-medium',
    inModalVersion ? 'text-12' : 'text-16',
  ]);
  const variantContainerClassName = useClassNames(['mt-12 flex', 'gap-12']);

  const finalSelectedOption: string = selectedOption ?? currentVariant.attributes?.[attribute];

  return (
    <div className={className}>
      <Typography className={labelClassName}>
        {formatMessage({ id: attributeString, defaultMessage: extractAttributeName(attributeString) })}
      </Typography>

      <div className={variantContainerClassName}>
        <Dropdown
          className="py-12"
          extraButtonClassName="py-27"
          customButtonElement={
            <DropdownButton
              isColorAttribute={isColorAttribute}
              selectedOption={isColorAttribute ? textToColor(finalSelectedOption).label : finalSelectedOption}
              backgroundColor={isColorAttribute ? textToColor(finalSelectedOption).code : undefined}
            />
          }
        >
          {options.map((option) => {
            return (
              <div key={`${currentVariant.id}-${currentVariant.sku}-${attribute}-${option}`} className="w-full">
                <Menu.Item>
                  <div className="bg-white hover:bg-neutral-200 active:bg-neutral-200">
                    <Button
                      variant="secondary-inverted"
                      size="full"
                      onClick={() => {
                        onClick?.(attribute, option);
                      }}
                      className="flex w-full items-center justify-start px-16 py-12"
                    >
                      <div className="flex items-center gap-8">
                        {isColorAttribute && <ColorBackground backgroundColor={textToColor(option).code} />}
                        <Typography className="text-14 font-normal text-primary-black">
                          {isColorAttribute ? textToColor(option).label : option}
                        </Typography>
                      </div>
                    </Button>
                  </div>
                </Menu.Item>
              </div>
            );
          })}
        </Dropdown>
      </div>
    </div>
  );
};

export default ProductAttribute;
